import { Link, useSearchParams } from 'react-router';
import { BellOff, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useRuleOffLink, useTurnOffRuleFromLink } from '@/hooks/useNotifications';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@/utils/apiError';

/**
 * The page a notification email's "Turn off this rule" link opens
 * (`/notifications/turn-off?token=…`).
 *
 * Public on purpose: the signed link is the credential, and someone reading their
 * mail on a phone should not have to sign in to stop it. It asks before it acts —
 * one button — because mail scanners open every link in a message, and a link that
 * turned the rule off by being opened would turn rules off that nobody asked to
 * (ruling, 2026-10-22). The link can only turn its one rule off; turning it back on
 * is the rules page's, behind a sign-in.
 */
export default function TurnOffNotificationRule() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const link = useRuleOffLink(token);
  const turnOff = useTurnOffRuleFromLink();
  const rule = turnOff.data ?? link.data;

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full text-center space-y-4">
        {!token ? (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Incomplete link</h1>
            <p className="text-sm text-muted-foreground">
              This link is missing part of what it needs. Open the one in your email again.
            </p>
          </>
        ) : link.isPending ? (
          <>
            <Loader2 className="h-10 w-10 text-muted-foreground mx-auto animate-spin" />
            <h1 className="text-lg font-semibold">Finding the rule…</h1>
          </>
        ) : link.isError ? (
          <>
            <XCircle className="h-10 w-10 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold">Couldn't open this link</h1>
            <p className="text-sm text-muted-foreground">{apiErrorMessage(link.error, 'This link is not valid.')}</p>
          </>
        ) : rule && !rule.enabled ? (
          <>
            <CheckCircle2 className="h-10 w-10 text-sev-4 mx-auto" />
            <h1 className="text-lg font-semibold">This rule is off</h1>
            <p className="text-sm break-words">{rule.sentence}</p>
            <p className="text-sm text-muted-foreground">
              It sends you nothing more. To turn it back on, sign in and open your notification rules.
            </p>
          </>
        ) : (
          <>
            <BellOff className="h-10 w-10 text-muted-foreground mx-auto" />
            <h1 className="text-lg font-semibold">Turn off this notification rule?</h1>
            <p className="text-sm break-words">{rule?.sentence}</p>
            <p className="text-sm text-muted-foreground">
              It stops sending you anything until you turn it back on. Your other rules are unchanged.
            </p>
            {turnOff.isError ? (
              <p className="text-sm text-destructive">{apiErrorMessage(turnOff.error, 'Could not turn the rule off.')}</p>
            ) : null}
            <Button
              disabled={turnOff.isPending}
              onClick={() => {
                turnOff.mutate(token);
              }}
            >
              {turnOff.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Turn off this rule
            </Button>
          </>
        )}
        <div>
          <Button asChild variant="link">
            <Link to="/notifications/rules">Manage your notification rules</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
